import { useCallback, useMemo } from "react";
import { I18n } from "i18n-js";
import { useRecoilValue } from "recoil";
import { DEFAULT_LOCALE } from "../i18n/config";
import { currentLang, currentTranslations } from "../atoms/i18n";
import { formatDuration, Locale } from "date-fns";
import frChLocale from "date-fns/locale/fr-CH";
// import enLocale from "date-fns/locale/en-US";
import { MissingBehavior } from "i18n-js/src/typing";
import { getLocales } from "expo-localization";
import { formatCurrency, FormatCurrencyType, formatWorkingTimeClassicSpec } from "../utils";
import { Buildable } from "ts-essentials";
import { Contract, WorkingTimeClassicType } from "../__generated__/graphql";

const locales = getLocales();

const SUPPORTED_LOCALES = [DEFAULT_LOCALE];

const getLocale = () => {
    const tag = getLocaleTag();
    switch (tag) {
        case "fr-CH":
            return frChLocale as unknown as Locale;
        // add other locales here
        default:
            return frChLocale as unknown as Locale;
    }
};

export const getLocaleTag = () => {
    const tag = locales?.[0]?.languageTag;
    if (SUPPORTED_LOCALES.includes(tag)) {
        return tag;
    }
    return DEFAULT_LOCALE;
};

class I18nInternal {
    i18n: I18n;
    locale: string;

    constructor(i18n: I18n) {
        this.i18n = i18n;
        this.locale = i18n.locale;
    }

    translate(key: string, options?: object) {
        return this.i18n.translate(key, options);
    }

    t(key: string, options?: object) {
        return this.i18n.t(key, options);
    }

    /**
     * translate with an enum value parameter
     * enum MyEnum {
     *     key1 = "key1",
     *     key2 = "key2",
     * }
     * x = MyEnum.key1
     * i18n.tx("my.value", x, "title") -> "my.value.key1.title"
     * This is used to avoid dynamic keys in translation code to check for existing translations keys usage in code
     * @param prefix
     * @param type
     * @param suffix
     */
    tx<P extends string, T extends string, S extends string | undefined = undefined>(
        prefix: P,
        type: T,
        suffix: S,
    ) {
        return `${prefix}.${type}${suffix ? `.${suffix}` : ""}` as S extends string
            ? `${P}.${T}.${S}`
            : `${P}.${T}`;
    }
}

/**
 * Hook with Suspense support
 */
export const useI18n = () => {
    const lang = useRecoilValue(currentLang);
    const translations = useRecoilValue(currentTranslations);
    return useMemo(
        () =>
            new I18nInternal(
                new I18n(
                    { [lang]: translations },
                    {
                        locale: lang,
                    },
                ),
            ),
        [lang, translations],
    );
};

export const useCurrencyFormatter = () => {
    return useCallback((params: FormatCurrencyType) => {
        return formatCurrency({ ...params, locale: getLocaleTag() });
    }, []);
};

export const useDurationFormatter = () => {
    return useCallback((durationInMinutes: number) => {
        const h = Math.floor(durationInMinutes / 60);
        const m = Math.round(durationInMinutes % 60);
        return formatDuration(
            { minutes: m, hours: h },
            {
                zero: false,
                locale: getLocale(),
                format: ["hours", "minutes"],
            },
        );
    }, []);
};

export const useDurationShortFormatter = () => {
    const i18n = useI18n();
    return useCallback(
        (durationInMinutes: number, dailyHours?: number) => {
            const h = Math.floor(durationInMinutes / 60);
            const m = Math.round(durationInMinutes % 60);
            let result = "";
            if (h > 0) {
                result += `${h}h`;
            }
            if (m > 0) {
                result += `${m.toString().padStart(2, "0")}m`;
            }
            if (dailyHours && h >= dailyHours) {
                const days = parseFloat((durationInMinutes / 60 / dailyHours).toFixed(1));
                const strDays = i18n.t("utils.working_time.classic.nb_days", { count: days });
                result += ` (${strDays})`;
            }
            return result;
        },
        [i18n],
    );
};

export const useNationalityFormatter = () => {
    const i18n = useI18n();
    return useCallback(
        (
            countryCode: string,
            gender?: "male" | "female" | "unknown" | null,
            missingBehavior?: MissingBehavior,
        ) => {
            const k = `nationalities.${countryCode.toUpperCase()}`;
            const base = i18n.t(k, { missingBehavior: missingBehavior });
            const [m, f] = base.split("|");
            if (gender == "female" && f) {
                return f;
            }
            if (gender == "male") {
                return m;
            }
            return base;
        },
        [i18n],
    );
};

export const useFormatContractWorkingTime = () => {
    const formatWorkingTimeClassicSpec = useFormatWorkingTimeClassicSpec();

    return useCallback(
        (contract?: Buildable<Contract> | null) => {
            if (contract?.workingTimeClassicSpec) {
                return formatWorkingTimeClassicSpec(contract.workingTimeClassicSpec);
            }

            return "";
        },
        [formatWorkingTimeClassicSpec],
    );
};

export const useFormatWorkingTimeClassicSpec = () => {
    const i18n = useI18n();

    return useCallback(
        (workingTimeSpec: Buildable<WorkingTimeClassicType>) =>
            formatWorkingTimeClassicSpec(workingTimeSpec, i18n),
        [i18n],
    );
};
export const useAllCountryCodes = (): Array<string> => {
    return useMemo(() => {
        return [
            "AL",
            "AD",
            "AT",
            "BY",
            "BE",
            "BA",
            "BG",
            "HR",
            "CY",
            "CZ",
            "DK",
            "EE",
            "FI",
            "FR",
            "DE",
            "GR",
            "HU",
            "IS",
            "IE",
            "IT",
            "XK",
            "LV",
            "LI",
            "LT",
            "LU",
            "MK",
            "MT",
            "MD",
            "MC",
            "ME",
            "NL",
            "NO",
            "PL",
            "PT",
            "RO",
            "RU",
            "SM",
            "RS",
            "SK",
            "SI",
            "ES",
            "SE",
            "CH",
            "UA",
            "GB",
        ];
    }, []);
};
